import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import { MapPin, Nfc, Route } from "lucide-react";

// Fix default-marker icon URLs (Webpack/CRA strips them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const METHOD_OPTIONS = [
  { value: "gps", label: "GPS" },
  { value: "nfc", label: "NFC" },
];

const DAYS_OPTIONS = [1, 7, 14, 30, 60, 90];

export default function TourScans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [clients, setClients] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [guardIds, setGuardIds] = useState([]);
  const [clientIds, setClientIds] = useState([]);
  const [portfolioIds, setPortfolioIds] = useState([]);
  const [methods, setMethods] = useState([]);
  const [days, setDays] = useState(7);
  const [mapRow, setMapRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const end = new Date();
      const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      const params = { start: start.toISOString(), end: end.toISOString(), limit: 500 };
      if (siteIds.length) params.site_id = siteIds.join(",");
      if (guardIds.length) params.guard_id = guardIds.join(",");
      if (methods.length) params.method = methods.join(",");
      if (clientIds.length) params.client_id = clientIds.join(",");
      if (portfolioIds.length) params.portfolio_id = portfolioIds.join(",");
      const r = await api.get("/tour-scans", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load tour scans"));
    } finally { setLoading(false); }
  }, [days, siteIds, guardIds, methods, clientIds, portfolioIds]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([
      api.get("/sites"),
      api.get("/guards"),
      api.get("/clients"),
      api.get("/portfolios").catch(() => ({ data: [] })),
    ]).then(([s, g, c, p]) => {
      setSites(s.data || []);
      setGuards(g.data || []);
      setClients(c.data || []);
      setPortfolios(p.data || []);
    }).catch(() => {});
  }, []);

  const siteOptions = useMemo(() =>
    sites
      .filter((s) => s.enabled_reports?.tours !== false)
      .map((s) => ({ value: s.id, label: s.name, sub: s.client_name || "" }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  [sites]);

  const guardOptions = useMemo(() =>
    guards
      .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  [guards]);

  const clientOptions = useMemo(() =>
    (clients || [])
      .filter((c) => c && c.id && c.name)
      .map((c) => ({ value: c.id, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  [clients]);

  const portfolioOptions = useMemo(() =>
    (portfolios || [])
      .filter((p) => p && p.id && p.name)
      .map((p) => ({ value: p.id, label: p.name, sub: (p.site_ids || []).length + " sites" }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  [portfolios]);

  const gpsCount = rows.filter((r) => r.method === "gps").length;
  const nfcCount = rows.filter((r) => r.method === "nfc").length;

  return (
    <div>
      <PageHeader
        title="Tour Scans"
        subtitle="Detailed per-scan feed — every GPS or NFC checkpoint hit logged by  on patrol. Click a row to see it on the map."
      />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Total scans (window)</div>
            <div className="font-display text-2xl font-black text-white" data-testid="tour-scans-total">{rows.length}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">GPS scans</div>
            <div className="font-display text-2xl font-black text-[#3B82F6]">{gpsCount}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">NFC scans</div>
            <div className="font-display text-2xl font-black text-[#22C55E]">{nfcCount}</div>
          </div>
          <div className="border border-[#27272A] bg-[#0A0A0A] p-3">
            <div className="label-mute">Distinct sites</div>
            <div className="font-display text-2xl font-black text-white">
              {new Set(rows.map((r) => r.site_id).filter(Boolean)).size}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3" data-testid="tour-scans-filters">
          <div>
            <div className="label-mute mb-1">Client</div>
            <MultiSelectDropdown
              testId="tour-scans-client-filter"
              label="All clients"
              options={clientOptions}
              selected={clientIds}
              onChange={setClientIds}
              searchable
              showSelectAll
              triggerClassName="min-w-[200px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Portfolio</div>
            <MultiSelectDropdown
              testId="tour-scans-portfolio-filter"
              label="All portfolios"
              options={portfolioOptions}
              selected={portfolioIds}
              onChange={setPortfolioIds}
              searchable
              showSelectAll
              triggerClassName="min-w-[200px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Site</div>
            <MultiSelectDropdown
              testId="tour-scans-site-filter"
              label="All sites"
              options={siteOptions}
              selected={siteIds}
              onChange={setSiteIds}
              searchable
              showSelectAll
              triggerClassName="min-w-[220px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Officer</div>
            <MultiSelectDropdown
              testId="tour-scans-guard-filter"
              label="All "
              options={guardOptions}
              selected={guardIds}
              onChange={setGuardIds}
              searchable
              showSelectAll
              triggerClassName="min-w-[200px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Method</div>
            <MultiSelectDropdown
              testId="tour-scans-method-filter"
              label="All methods"
              options={METHOD_OPTIONS}
              selected={methods}
              onChange={setMethods}
              showSelectAll
              triggerClassName="min-w-[150px]"
            />
          </div>
          <div>
            <div className="label-mute mb-1">Window (days)</div>
            <select
              data-testid="tour-scans-days"
              className={inputClass}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {DAYS_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="ml-auto self-end">
            <a
              href={(() => {
                const end = new Date();
                const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
                const p = new URLSearchParams({ start: start.toISOString(), end: end.toISOString() });
                if (siteIds.length) p.set("site_id", siteIds.join(","));
                if (guardIds.length) p.set("guard_id", guardIds.join(","));
                if (methods.length) p.set("method", methods.join(","));
                if (clientIds.length) p.set("client_id", clientIds.join(","));
                if (portfolioIds.length) p.set("portfolio_id", portfolioIds.join(","));
                return `/api/tour-scans/export.csv?${p.toString()}`;
              })()}
              data-testid="tour-scans-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#27272A] hover:border-[#FF3B30] text-xs uppercase tracking-widest text-zinc-300 hover:text-[#FF3B30] transition-colors"
              title="Download the currently-filtered scans as CSV"
            >
              <Route className="size-3.5" /> Export CSV
            </a>
          </div>
        </div>

        <div className="border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="tour-scans-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th className="px-4 py-3 text-left"><div className="label-mute">Time</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Officer</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Site</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Stop</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Method</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">NFC UID</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">GPS</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Notes</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {loading && <tr><td colSpan={8} className="text-center py-12 text-zinc-500 scanlines">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-16 text-zinc-500 scanlines">No tour scans match the current filters.</td></tr>
              )}
              {rows.map((r) => {
                const hasGps = r.lat != null && r.lng != null;
                return (
                  <tr
                    key={r.id}
                    className={`hover:bg-[#1c1c1e] ${hasGps ? "cursor-pointer" : ""}`}
                    data-testid={`tour-scan-row-${r.id}`}
                    onClick={() => hasGps && setMapRow(r)}
                    title={hasGps ? "Click to view on map" : "No GPS coordinates recorded"}
                  >
                    <td className="px-4 py-3 font-mono text-zinc-400 text-xs whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-zinc-200 text-sm">
                      {r.guard_name || "—"}
                      {r.badge_no && <div className="text-[10px] text-zinc-600 font-mono">{r.badge_no}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-200 text-sm">
                      {r.site_name || "—"}
                      {r.client_name && <div className="text-[10px] text-zinc-600 font-mono">{r.client_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-200 text-sm">{r.stop_name || "—"}</td>
                    <td className="px-4 py-3">
                      {r.method === "nfc" ? (
                        <PillBadge tone="green"><Nfc className="inline size-3 mr-1" />NFC</PillBadge>
                      ) : r.method === "gps" ? (
                        <PillBadge tone="blue"><MapPin className="inline size-3 mr-1" />GPS</PillBadge>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500 max-w-[140px] truncate">{r.nfc_tag_id || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                      {hasGps ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setMapRow(r); }}
                          data-testid={`tour-scan-map-${r.id}`}
                          title="Open on map"
                          className="inline-flex items-center gap-1.5 border border-[#27272A] hover:border-[#FF3B30] px-2 py-1 rounded text-zinc-300 hover:text-white hover:bg-[#FF3B30]/10 transition-colors"
                        >
                          <MapPin className="size-3 text-[#FF3B30]" />
                          <span>{r.lat.toFixed(4)}, {r.lng.toFixed(4)}</span>
                        </button>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs max-w-xs truncate">{r.notes || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {mapRow && (
        <Modal
          open
          onClose={() => setMapRow(null)}
          title={`${mapRow.stop_name || "Scan"} — ${new Date(mapRow.created_at).toLocaleString()}`}
          titleIcon={<Route className="size-4 text-[#FF3B30]" />}
          maxWidth="3xl"
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono text-zinc-400">
              <div><div className="label-mute">Officer</div><div className="text-zinc-200 mt-1">{mapRow.guard_name || "—"}</div></div>
              <div><div className="label-mute">Site</div><div className="text-zinc-200 mt-1">{mapRow.site_name || "—"}</div></div>
              <div><div className="label-mute">Method</div><div className="text-zinc-200 mt-1 uppercase">{mapRow.method}</div></div>
              <div><div className="label-mute">GPS</div><div className="text-zinc-200 mt-1">{mapRow.lat?.toFixed(6)}, {mapRow.lng?.toFixed(6)}</div></div>
            </div>
            <div className="border border-[#27272A]" style={{ height: 380 }}>
              <MapContainer center={[mapRow.lat, mapRow.lng]} zoom={17} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
                />
                <Marker position={[mapRow.lat, mapRow.lng]} />
              </MapContainer>
            </div>
            <div className="flex items-center justify-between">
              <a
                href={`https://www.google.com/maps?q=${mapRow.lat},${mapRow.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="tour-scan-open-google-maps"
                className="inline-flex items-center gap-1.5 text-xs text-[#FF3B30] hover:underline"
              >
                <MapPin className="size-3.5" />
                Open in Google Maps
              </a>
              <GhostButton onClick={() => setMapRow(null)}>Close</GhostButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
